import { forwardRef, HTMLAttributes, ReactNode } from "react";

export type Props = HTMLAttributes<HTMLDivElement> & {
  /** Whether the requirement is met */
  isMet: boolean;
  /** The requirement message */
  message: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the message */
  messageClassName?: string;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Render prop for the check icon */
  renderIcon?: (props: { isMet: boolean }) => ReactNode;
  /** Render prop for the message */
  renderMessage?: (props: { message: string; className?: string }) => ReactNode;
};

/**
 * Base requirement check component with no styling.
 * Displays a validation indicator with a message.
 * Uses render props for icon and message customization.
 */
export const RequirementCheckBase = forwardRef<HTMLDivElement, Props>(
  (
    {
      isMet,
      message,
      className,
      messageClassName,
      "data-testid": testId = "requirement-check-base",
      renderIcon,
      renderMessage,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={className}
        data-testid={testId}
        data-met={isMet}
        {...props}
      >
        {renderIcon ? (
          renderIcon({ isMet })
        ) : (
          <span data-testid={`${testId}-icon`}>{isMet ? "✓" : "○"}</span>
        )}
        {renderMessage ? (
          renderMessage({ message, className: messageClassName })
        ) : (
          <span data-testid={`${testId}-message`} className={messageClassName}>
            {message}
          </span>
        )}
      </div>
    );
  }
);

RequirementCheckBase.displayName = "RequirementCheckBase";

